<template>
  <ResourceDialog
    ref="dialog"
    :min-width="$vuetify.display.smAndDown ? undefined : '500px'"
    title="Your summary"
    closeable
  >
    <template #form>
      <template v-if="programs.length > 0">
        <div>
          <strong>{{ provider.name }}</strong>
          {{ $bct('currently reports the following enrollments and openings:') }}
        </div>

        <v-alert
          v-if="activeErrors.length > 0"
          class="mt-2"
          density="compact"
          type="error"
          variant="tonal"
        >
          {{ activeErrors.join(', ') }}
        </v-alert>

        <v-card
          v-for="program in programs"
          :key="program.id"
          class="mt-4 mx-2"
          border
          tile
        >
          <template #title>
            <span class="fs-18">{{ program.name }}</span>
          </template>

          <v-card-text class="text-body-1">
            <v-row
              class="d-flex align-center justify-space-between"
              dense
            >
              <v-col
                class="py-0"
                cols="6"
              >
                {{ $bct('Enrollments') }}
              </v-col>
              <v-col
                class="text-end py-0"
                cols="6"
              >
                {{ program.enrollments_count || 0 }}
              </v-col>
            </v-row>

            <v-row
              class="d-flex align-center justify-space-between"
              dense
            >
              <v-col
                class="py-0"
                cols="6"
              >
                {{ $bct('Openings') }}
              </v-col>
              <v-col
                class="text-end py-0"
                cols="6"
              >
                {{ program.openings_count || 0 }}
              </v-col>
            </v-row>

            <v-row
              class="d-flex align-center justify-space-between"
              dense
            >
              <v-col
                class="py-0"
                cols="6"
              >
                {{ $bct('Total') }}
              </v-col>
              <v-col
                class="text-end py-0"
                cols="6"
              >
                {{ getCountTotal(program) }}
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </template>

      <template v-else>
        <strong>{{ provider.name }}</strong>
        {{
          $bct(
            `has no configured ${terms.programs.toLowerCase()}. Edit your ${terms.programs.toLowerCase()} or contact your administrator.`,
          )
        }}
      </template>
    </template>

    <template #actions>
      <v-btn
        v-if="route.name == 'ProviderShow'"
        @click="dialog.close()"
        :block="$vuetify.display.smAndDown"
        :disabled="processing"
        :loading="processing"
        class="mb-2"
        color="primary"
        variant="outlined"
      >
        {{ $bct('Cancel') }}
      </v-btn>
      <v-btn
        v-else-if="programs.length === 0"
        @click="goToProviderProfileProgramsTab"
        :block="$vuetify.display.smAndDown"
        :disabled="processing"
        :loading="processing"
        class="mb-2"
        color="primary"
        prepend-icon="edit"
        variant="outlined"
      >
        {{ $bct(`Edit my ${terms.programs.toLowerCase()}`) }}
      </v-btn>
      <v-btn
        v-else
        @click="goToProviderProfileOpeningsAndEnrollmentsTab"
        :block="$vuetify.display.smAndDown"
        :disabled="processing"
        :loading="processing"
        class="mb-2"
        color="primary"
        prepend-icon="edit"
        variant="outlined"
      >
        {{ $bct('Update my info') }}
      </v-btn>

      <v-spacer />

      <v-btn
        @click="emit('openings-confirmed')"
        :block="$vuetify.display.smAndDown"
        :disabled="programs.length === 0 || processing"
        :loading="processing"
        :style="{ 'margin-inline-start': $vuetify.display.smAndDown ? '0' : undefined }"
        class="mb-2"
        color="primary"
        data-testid="save-button"
      >
        {{ $bct('Confirm and submit') }}
      </v-btn>
    </template>
  </ResourceDialog>
</template>

<script setup>
import { useRoute, useRouter } from 'vue-router';

import ResourceDialog from '@/shared/components/form/ResourceDialog.vue';
import useTerms from '@/shared/composables/useTerms';

const $bct = inject('translator');

defineExpose({
  open,
  close,
});

const props = defineProps({
  activeErrors: {
    type: Array,
    default: () => [],
  },
  processing: {
    type: Boolean,
    default: false,
  },
  programs: {
    type: Array,
    default: () => [],
  },
  provider: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['openings-confirmed']);

const route = useRoute();
const router = useRouter();
const { terms } = useTerms();

const dialog = ref(null);

function getCountTotal(program) {
  return parseInt(program.enrollments_count || 0, 10) + parseInt(program.openings_count || 0, 10);
}

async function goToProviderProfileOpeningsAndEnrollmentsTab() {
  await router.push({
    name: 'ProviderShow',
    params: { providerId: props.provider.id },
    query: { tab: 'openings-and-enrollments', backTo: 'ProviderIndex' },
  });
}

async function goToProviderProfileProgramsTab() {
  await router.push({
    name: 'ProviderShow',
    params: { providerId: props.provider.id },
    query: { tab: 'programs', backTo: 'ProviderIndex' },
  });
}

function open() {
  dialog.value.open();
}

function close() {
  dialog.value.close();
}
</script>
